import { ConfigProvider } from "antd";
import { useApplicationViewSettings } from "../../../../../containers/ApplicationPage/ApplicationViewContext";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Descriptions, Select, Space, Typography } from "antd";
import { useSelector } from "react-redux";
import { DeleteOutlined, MinusOutlined, PlusOutlined, PaperClipOutlined } from "@ant-design/icons";
import _ from "lodash";
import { ApplicationDataConverter, CargoCharacteristicModel } from "../../../../../viewModels/Applications/ApplicationModel";
import { CargoDimensionsMode } from "../../../../../dto/Applications/ApplicationDto";
import { makeSelectHost } from "../../../../../containers/App/selectors";
import getDocumentUrl from "../../../../../utils/getDocumentUrl";
import UploadFileDragger from "../../../../UploadFileDragger";
import LPCustomNumericFormat from "../../../../LongpollControls/LPCustomNumericFormat";
import LPInput from "../../../../LongpollControls/LPInput";
import LPTextArea from "../../../../LongpollControls/LPTextArea";
import { AttachDocumentDataConverter } from "../../../../../viewModels/Documents/AttachDocumentModel";
import type { ApplicationSummaryBlockProps } from "../../types";
import CargoPlanningButton from "../../../../../containers/ApplicationPage/components/CargoPlanningButton";
import CargoPlanningFileError from "../../../../../containers/ApplicationPage/components/CargoPlanningFileError";
import { useCargoPlanning } from "../../../../../containers/ApplicationPage/components/CargoPlanningProvider";
import { useDispatch } from "react-redux";
import applicationSlice from "../../../../../containers/ApplicationPage/reducer";

const CargoCharacteristics = React.memo(({ application, onChange, canEditApplicationFields, onCommit }: ApplicationSummaryBlockProps) => {
  const { t } = useTranslation("translations");
  const viewSettings = useApplicationViewSettings();
  const host = useSelector(makeSelectHost());
  const planning = useCargoPlanning();
  const dispatch = useDispatch();
  const documentsDisabled = planning?.saving || planning?.checking || planning?.syncState === "SYNCING";
  const isFtlApplication = application.type === "FTL";
  const cargoDimensionsMode = application.cargoInfo?.cargoDimensionsMode || "ENTIRE_CARGO";
  const isCargoDimensionsFromFile = isFtlApplication && cargoDimensionsMode === "FROM_FILE";
  const isOneCargoUnitMode = isFtlApplication && cargoDimensionsMode === "ONE_CARGO_UNIT";
  const cargoDimensionsModeOptions: Array<{ value: CargoDimensionsMode; label: React.ReactNode }> = [
    { value: "ENTIRE_CARGO", label: t("whole_cargo") },
    { value: "ONE_CARGO_UNIT", label: t("one_unit_of_cargo") },
    { value: "FROM_FILE", label: t("attach_document") },
  ];
  const cargoDimensionsModeLabel = cargoDimensionsModeOptions.find((x) => x.value === cargoDimensionsMode)?.label || "-";
  const displayedCargoCharacteristics =
    isFtlApplication && !isOneCargoUnitMode
      ? (application.cargoInfo?.cargoCharacteristics || [{}]).slice(0, 1)
      : application.cargoInfo?.cargoCharacteristics || [{}];
  const canAddCargoCharacteristic = canEditApplicationFields && (!isFtlApplication || isOneCargoUnitMode);
  const columnCount = canAddCargoCharacteristic ? 6 : 5;
  const handleChangeCargoDimensionsMode = React.useCallback(
    (mode: CargoDimensionsMode) => {
      if (onChange) {
        const currentCargoCharacteristics = _.cloneDeep(application.cargoInfo?.cargoCharacteristics || []);
        const nextCargoInfo = {
          ...application.cargoInfo,
          cargoDimensionsMode: mode,
        };

        if (mode === "FROM_FILE") {
          nextCargoInfo.cargoCharacteristics = undefined;
          nextCargoInfo.totalWeight = undefined;
          nextCargoInfo.totalVolume = undefined;
        } else {
          nextCargoInfo.cargoDimensionsDocuments = undefined;

          if (mode === "ENTIRE_CARGO") {
            nextCargoInfo.cargoCharacteristics = [{ ...(currentCargoCharacteristics[0] || {}), count: 1 }];
          } else {
            nextCargoInfo.cargoCharacteristics = currentCargoCharacteristics.length > 0 ? currentCargoCharacteristics : [{}];
          }

          nextCargoInfo.totalWeight = ApplicationDataConverter.calculateTotalWeight(nextCargoInfo.cargoCharacteristics);
          nextCargoInfo.totalVolume = ApplicationDataConverter.calculateTotalVolume(nextCargoInfo.cargoCharacteristics);
        }

        onChange({
          ...application,
          cargoInfo: nextCargoInfo,
        });

        onCommit?.();
      }
    },
    [application, onCommit, onChange],
  );
  const handleChangeCargoCharacteristic = React.useCallback(
    (key: keyof CargoCharacteristicModel, value: number | undefined, index: number) => {
      if (onChange) {
        const cargoCharacteristics = _.cloneDeep(application.cargoInfo?.cargoCharacteristics || []);
        cargoCharacteristics[index] = cargoCharacteristics[index] || {};
        cargoCharacteristics[index][key] = value;
        onChange({
          ...application,
          cargoInfo: {
            ...application.cargoInfo,
            cargoCharacteristics,
            totalWeight: ApplicationDataConverter.calculateTotalWeight(cargoCharacteristics),
            totalVolume: ApplicationDataConverter.calculateTotalVolume(cargoCharacteristics),
          },
        });
      }
    },
    [onChange, application],
  );
  const handleAddCargoCharacteristic = React.useCallback(() => {
    if (onChange) {
      const cargoCharacteristics = _.cloneDeep(application.cargoInfo?.cargoCharacteristics || []);
      cargoCharacteristics.push({});

      onChange({
        ...application,
        cargoInfo: {
          ...application.cargoInfo,
          cargoCharacteristics,
          totalWeight: ApplicationDataConverter.calculateTotalWeight(cargoCharacteristics),
          totalVolume: ApplicationDataConverter.calculateTotalVolume(cargoCharacteristics),
        },
      });
      onCommit?.();
    }
  }, [onChange, application, onCommit]);
  const handleRemoveCargoCharacteristic = React.useCallback(
    (index: number) => {
      if (onChange) {
        const cargoCharacteristics = _.cloneDeep(application.cargoInfo?.cargoCharacteristics || []);
        cargoCharacteristics.splice(index, 1);
        onChange({
          ...application,
          cargoInfo: {
            ...application.cargoInfo,
            cargoCharacteristics,
            totalWeight: ApplicationDataConverter.calculateTotalWeight(cargoCharacteristics),
            totalVolume: ApplicationDataConverter.calculateTotalVolume(cargoCharacteristics),
          },
        });

        onCommit?.();
      }
    },
    [onChange, application, onCommit],
  );
  return (
    <Space orientation="vertical" style={{ width: "100%" }}>
      {canAddCargoCharacteristic && (
        <Button size="small" type="text" icon={<PlusOutlined />} onClick={handleAddCargoCharacteristic}>
          {t("add_unit_of_cargo")}
        </Button>
      )}
      <ConfigProvider
        theme={{
          token: { fontSize: 12 },
          components: {
            Select: {
              activeBorderColor: "transparent",
              activeOutlineColor: "transparent",
            },
            Input: {
              activeBorderColor: "transparent",
              activeShadow: "none",
              inputFontSize: 12,
              inputFontSizeSM: 12,
              inputFontSizeLG: 12,
              paddingBlock: 0,
              paddingInline: 0,
              paddingBlockSM: 0,
              paddingInlineSM: 0,
              paddingBlockLG: 0,
              paddingInlineLG: 0,
            },
            InputNumber: {
              activeBorderColor: "transparent",
              activeShadow: "none",
              inputFontSize: 12,
              inputFontSizeSM: 12,
              inputFontSizeLG: 12,
              paddingBlock: 0,
              paddingInline: 0,
              paddingBlockSM: 0,
              paddingInlineSM: 0,
              paddingBlockLG: 0,
              paddingInlineLG: 0,
            },
          },
        }}
        select={{ styles: { root: { padding: 0 } } }}
        textArea={{ styles: { textarea: { minHeight: 22 } } }}
        descriptions={{
          styles: {
            label: { padding: "6px 8px" },
            content: { padding: "6px 8px" },
          },
        }}
      >
        <Descriptions
          bordered
          column={columnCount}
          items={[
            isFtlApplication && {
              label: t("specify_cargo_dimensions"),
              span: columnCount,

              children: canEditApplicationFields ? (
                <Select
                  variant="borderless"
                  value={cargoDimensionsMode}
                  options={cargoDimensionsModeOptions}
                  disabled={documentsDisabled}
                  suffixIcon={null}
                  onChange={handleChangeCargoDimensionsMode}
                  style={{ width: "100%" }}
                />
              ) : (
                cargoDimensionsModeLabel
              ),
            },
            isCargoDimensionsFromFile && {
              label: t("attached_documents"),
              span: columnCount,

              children: (
                <>
                  {canEditApplicationFields && (
                    <UploadFileDragger
                      styles={{ trigger: { border: 0, background: "transparent", textAlign: "left" } }}
                      showUploadList={false}
                      multiple
                      disabled={documentsDisabled}
                      onUploadStart={() =>
                        dispatch(applicationSlice.actions.changeCargoPlanningUploads({ applicationId: application.id, delta: 1 }))
                      }
                      onUploadEnd={() =>
                        dispatch(applicationSlice.actions.changeCargoPlanningUploads({ applicationId: application.id, delta: -1 }))
                      }
                      onAttachFiles={(files) => {
                        onChange({
                          ...application,
                          cargoInfo: {
                            ...application.cargoInfo,
                            cargoDimensionsDocuments: (application.cargoInfo?.cargoDimensionsDocuments || []).concat(
                              AttachDocumentDataConverter.convertDocumentsToFormData(
                                files.map((x) => ({ id: undefined!, documentId: x.id, documentName: x.name })),
                                host,
                              ),
                            ),
                          },
                        });
                        onCommit?.();
                      }}
                      content={
                        <Button size="small" icon={<PaperClipOutlined />} tabIndex={-1}>
                          {t("attach_files")}
                        </Button>
                      }
                    />
                  )}
                  <Space orientation="vertical" size={2}>
                    {(application.cargoInfo?.cargoDimensionsDocuments || []).map((x, index) => (
                      <Space key={index} align="center">
                        {viewSettings ? (
                          <span>{x.name}</span>
                        ) : (
                          <a target="_blank" href={getDocumentUrl(x.response?.id!, host)} rel="noreferrer">
                            {x.name}
                          </a>
                        )}
                        <CargoPlanningFileError documentId={x.response?.id} />
                        {canEditApplicationFields && (
                          <Button
                            icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                            danger
                            type="text"
                            disabled={documentsDisabled}
                            style={{ height: 16, width: 16 }}
                            onClick={() => {
                              const copy = _.clone(application.cargoInfo?.cargoDimensionsDocuments || []);
                              copy.splice(index, 1);

                              onChange({
                                ...application,
                                cargoInfo: {
                                  ...application.cargoInfo,
                                  cargoDimensionsDocuments: copy,
                                },
                              });
                              onCommit?.();
                            }}
                          />
                        )}
                      </Space>
                    ))}
                  </Space>
                  {application.status !== "DRAFT" && <CargoPlanningButton application={application} />}
                </>
              ),
            },
            !isCargoDimensionsFromFile &&
              displayedCargoCharacteristics.map((x, index) => [
                {
                  label: `№${index + 1} · ${t("weight")}`,
                  key: `weight_${index}`,
                  styles: { label: { whiteSpace: "nowrap" } },
                  children: canEditApplicationFields ? (
                    <LPCustomNumericFormat
                      value={x.weight}
                      variant="borderless"
                      thousandSeparator=" "
                      decimalScale={2}
                      allowNegative={false}
                      onChange={(v) => {
                        handleChangeCargoCharacteristic("weight", v, index);
                        onCommit?.();
                      }}
                    />
                  ) : (
                    x.weight || "-"
                  ),
                },
                {
                  label: t("count_unit"),
                  key: `count_${index}`,
                  styles: { label: { whiteSpace: "nowrap" } },
                  children: canEditApplicationFields ? (
                    <LPCustomNumericFormat
                      value={x.count}
                      variant="borderless"
                      thousandSeparator=" "
                      decimalScale={0}
                      allowNegative={false}
                      onChange={(v) => {
                        handleChangeCargoCharacteristic("count", v, index);
                        onCommit?.();
                      }}
                    />
                  ) : (
                    x.count || "-"
                  ),
                },
                {
                  label: t("length"),
                  key: `length_${index}`,
                  styles: { label: { whiteSpace: "nowrap" } },
                  children: canEditApplicationFields ? (
                    <LPCustomNumericFormat
                      value={x.length}
                      variant="borderless"
                      thousandSeparator=" "
                      decimalScale={2}
                      allowNegative={false}
                      onChange={(v) => {
                        handleChangeCargoCharacteristic("length", v, index);
                        onCommit?.();
                      }}
                    />
                  ) : (
                    x.length || "-"
                  ),
                },
                {
                  label: t("width"),
                  key: `width_${index}`,
                  styles: { label: { whiteSpace: "nowrap" } },
                  children: canEditApplicationFields ? (
                    <LPCustomNumericFormat
                      value={x.width}
                      variant="borderless"
                      thousandSeparator=" "
                      decimalScale={2}
                      allowNegative={false}
                      onChange={(v) => {
                        handleChangeCargoCharacteristic("width", v, index);
                        onCommit?.();
                      }}
                    />
                  ) : (
                    x.width || "-"
                  ),
                },
                {
                  label: t("height"),
                  key: `height_${index}`,
                  styles: { label: { whiteSpace: "nowrap" } },
                  children: canEditApplicationFields ? (
                    <LPCustomNumericFormat
                      value={x.height}
                      variant="borderless"
                      thousandSeparator=" "
                      decimalScale={2}
                      allowNegative={false}
                      onChange={(v) => {
                        handleChangeCargoCharacteristic("height", v, index);
                        onCommit?.();
                      }}
                    />
                  ) : (
                    x.height || "-"
                  ),
                },
                canAddCargoCharacteristic && {
                  key: `delete${index}`,
                  styles: { label: { width: 0, padding: 0, borderInlineEnd: 0 }, content: { width: 32 } },
                  children: (
                    <Button
                      onClick={() => handleRemoveCargoCharacteristic(index)}
                      type="text"
                      size="small"
                      icon={<MinusOutlined />}
                      aria-label="Удалить груз"
                    />
                  ),
                },
              ]),
            !isCargoDimensionsFromFile && {
              span: 1,
              label: t("total_weight"),
              children: canEditApplicationFields ? (
                <LPCustomNumericFormat
                  value={application.cargoInfo?.totalWeight}
                  variant="borderless"
                  thousandSeparator=" "
                  decimalScale={2}
                  allowNegative={false}
                  onChange={(v) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, totalWeight: v } });
                    onCommit?.();
                  }}
                />
              ) : (
                application.cargoInfo?.totalWeight || "-"
              ),
            },
            !isCargoDimensionsFromFile && {
              span: 1,
              label: t("total_volume"),
              children: canEditApplicationFields ? (
                <LPCustomNumericFormat
                  value={application.cargoInfo?.totalVolume}
                  variant="borderless"
                  thousandSeparator=" "
                  decimalScale={6}
                  allowNegative={false}
                  onChange={(v) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, totalVolume: v } });
                    onCommit?.();
                  }}
                />
              ) : (
                application.cargoInfo?.totalVolume || "-"
              ),
            },
            {
              label: t("cargo_description"),
              span: 2,

              children: canEditApplicationFields ? (
                <LPInput
                  value={application.cargoInfo?.cargoDescription}
                  variant="borderless"
                  onChange={(e) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, cargoDescription: e.target.value } });
                    onCommit?.();
                  }}
                />
              ) : (
                application.cargoInfo?.cargoDescription || "-"
              ),
            },
            {
              label: t("tn_ved_code"),
              span: "filled" as const,

              children: canEditApplicationFields ? (
                <LPInput
                  value={application.cargoInfo?.tnVedCode}
                  variant="borderless"
                  onChange={(e) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, tnVedCode: e.target.value } });
                    onCommit?.();
                  }}
                />
              ) : (
                application.cargoInfo?.tnVedCode || "-"
              ),
            },
            application.type === "FTL" && {
              label: t("is_oversized_cargo"),
              span: columnCount,

              styles: { label: { whiteSpace: "nowrap" } },
              children: canEditApplicationFields ? (
                <Select
                  variant="borderless"
                  value={application.oversize}
                  options={[
                    {
                      value: true,
                      label: t("yes"),
                    },
                    {
                      value: false,
                      label: t("no"),
                    },
                  ]}
                  suffixIcon={null}
                  onChange={(v) => {
                    onChange({
                      ...application,
                      oversize: v,
                    });
                    onCommit?.();
                  }}
                  style={{ width: "100%" }}
                />
              ) : (
                <Typography.Text style={{ whiteSpace: "pre-wrap" }}>{application.oversize ? t("yes") : t("no")}</Typography.Text>
              ),
            },
            {
              label: t("cargo_comment"),
              span: columnCount,

              styles: { label: { whiteSpace: "nowrap" } },
              children: canEditApplicationFields ? (
                <LPTextArea
                  autoSize
                  value={application.cargoInfo?.comment}
                  variant="borderless"
                  onChange={(e) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, comment: e.target.value } });
                    onCommit?.();
                  }}
                />
              ) : (
                <Typography.Text style={{ whiteSpace: "pre-wrap" }}>{application.cargoInfo?.comment || "-"}</Typography.Text>
              ),
            },
          ]
            .flat(3)
            .filter((item) => !!item)}
        />
      </ConfigProvider>
    </Space>
  );
});
export default CargoCharacteristics;
